import { useSyncExternalStore } from "react"
import { breakpointWidth } from "../ui/breakpoints.ts"

/**
 * 창 폭을 보는 화면들이 함께 쓰는 구독 하나.
 *
 * 전에는 표(DataTable)마다 제 resize 리스너를 달고 실제 폭을 state 로 들고 있었다.
 * 창을 끌면 1px 마다 화면에 있는 표가 전부 다시 그려졌고, 휴대폰에서는 주소창이 접힐 때도
 * resize 가 온다. 이제 리스너는 **하나**이고, 값은 구간 기준선으로 내려 두므로
 * (`breakpointWidth`) 구간을 넘을 때만 다시 그린다.
 *
 * CSS 미디어 쿼리로는 "칸을 빼고 남은 것으로 카드를 만든다"를 표현할 수 없어 폭을 본다.
 */

const SERVER_WIDTH = 1440

let current = read()
const listeners = new Set<() => void>()
let frame = 0

function read(): number {
  return typeof window === "undefined" ? SERVER_WIDTH : breakpointWidth(window.innerWidth)
}

function publish(): void {
  const next = read()
  if (next === current) return
  current = next
  for (const listener of listeners) listener()
}

/** resize 는 끌 때 연달아 온다. 한 프레임에 한 번만 본다. */
function onResize(): void {
  if (frame !== 0) return
  frame = window.requestAnimationFrame(() => {
    frame = 0
    publish()
  })
}

function subscribe(listener: () => void): () => void {
  const first = listeners.size === 0
  listeners.add(listener)
  if (first) {
    window.addEventListener("resize", onResize)
    // 모듈을 읽어 둔 뒤로 창이 바뀌었을 수 있다(늦게 붙는 화면).
    publish()
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      window.removeEventListener("resize", onResize)
      if (frame !== 0) {
        window.cancelAnimationFrame(frame)
        frame = 0
      }
    }
  }
}

/** 지금 창 폭(구간 기준선으로 내린 값). 배치 판단에 그대로 넣어 쓴다. */
export function useViewportWidth(): number {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => SERVER_WIDTH,
  )
}
